import { Howl } from "howler";
import { GameServices } from "game/GameServices";
import { random } from "lodash";
import { CashWedgeID, FreeSpinWedgeID, JackpotWedgeID, SymbolID, WedgeID } from "servertypes/SharedTypes";
import { Tween } from "@westpierstudio/luma/createjs";
import { Signal } from "type-signals";

const SPIN_SOUNDS = 20;
const WHEEL_CLICK_SOUNDS = 3;

type SoundSettings = {
    config: {
        precogFadeOutSpinVolume: number,
        precogFadeOutSpinTime: number,
        precogFadeInSpinDelay: number,
        precogFadeInSpinTime: number,
        precogTeaseFadeInSpinDelay: number,
        fadeInPrecogReelSpinDelay: number,
        fadeInPrecogReelSpinTime: number,
        bonusThemeStartDelay: number,
        bonusThemeFadeTime: number,
        bonusThemeSymbolWinFadeInDelay: number,
        bonusThemeSymbolWinFadeInVolume: number,
        scrollOpenDelay: number,
        bonusSymbolCoinIntoBonusVolume: number,
        bonusSymbolCoinWooshDelay: number,
        bonusBellRetriggerLength: number
    },
    data: {[key: string]: {name: string, volume: number}}
}

class SoundSignals {
    bonusBellRetriggerComplete = new Signal();
}

export class SoundPlayer {
    private readonly settings: SoundSettings;
    private sounds: Record<string,Howl> = {};
    private spinIndex = -1;
    private multiPanelIndex = -1;
    private winningSymbolSoundID?: SymbolID;
    private winSoundObject = {};
    private bonusFadeObject = {};
    private lastWheelClick = -Infinity;
    private maxWheelClicksPerSecond = 14;
    signals = new SoundSignals();

    constructor(protected readonly services: GameServices) {
        this.settings = this.services.resources.getSetting(`sounds`);
    }

    jackpotCoinCollision(coinNumber: number) {
        let collisionSound = `coinBowlCollision0`;
        if (coinNumber > 75) {
            collisionSound = `coinBowlCollision2`;
        } else if (coinNumber > 20) {
            collisionSound = `coinBowlCollision1`;
        }

        const sound = this.play(collisionSound, false, undefined, true);
        if (sound) {
            sound.on(`end`, () => sound.unload());
        }
    }

    spin(): void {

        this.spinIndex = (this.spinIndex + 1) % SPIN_SOUNDS;
        this.play(`spin${this.spinIndex}`);
    }

    stopSpin(): void {

        if (this.spinIndex > 0) {

            const {sound} = this.getSoundData(`spin${this.spinIndex}`);
            sound?.stop();
        }
    }

    bonusSymbolLand(reelIndex: number): void {
        this.play(`bonusSymbolLand${reelIndex}`);
    }

    reelStop(): void {
        this.play(`reelStop`);
    }

    skipSpin(freeSpin: boolean): void {

        Tween.removeTweens(this);

        this.stopSpin();

        let {sound} = this.getSoundData(`precogTease`);
        sound?.stop();

        ({sound} = this.getSoundData(`precog`));
        sound?.stop();

        ({sound} = this.getSoundData(`precogReelSpin`));
        sound?.stop();

        ({sound} = this.getSoundData(`laugh`));
        sound?.stop();

        if (freeSpin) {

            const {sound, volume} = this.getSoundData(`bonusTheme`);
            if (sound?.volume() !== volume) {
                sound?.fade(0, volume, this.settings.config.precogFadeInSpinTime);
            }
        }
    }

    precogTease(freeSpin: boolean): void {

        this.play(`precogTease`);

        const newVolume = this.fadeOutSpinSound(freeSpin);

        if (freeSpin) {

            const {sound, volume} = this.getSoundData(`bonusTheme`);

            Tween.get(this)
            .wait(this.settings.config.precogTeaseFadeInSpinDelay)
            .call(() => {
                sound?.fade(newVolume, volume, this.settings.config.precogFadeInSpinTime);
            });
        }
    }

    precog(freeSpin: boolean): void {

        this.play(`precog`);
        this.play(`laugh`);

        const newVolume = this.fadeOutSpinSound(freeSpin);

        if (freeSpin) {

            const {sound, volume} = this.getSoundData(`bonusTheme`);

            Tween.get(this)
            .wait(this.settings.config.precogFadeInSpinDelay)
            .call(() => {
                sound?.fade(newVolume, volume, this.settings.config.precogFadeInSpinTime);
            });
        }

        Tween.get(this)
        .wait(this.settings.config.fadeInPrecogReelSpinDelay)
        .call(() => {

            this.play(`precogReelSpin`, false, 1);

            const {sound, volume} = this.getSoundData(`precogReelSpin`);
            sound?.fade(1, volume, this.settings.config.fadeInPrecogReelSpinTime);
        });
    }

    multiplierPanelSymbolMove() {
        this.play(`multiplierPanelPrizeCollectWoosh`);
    }

    bonusSymbolCoin(intoBonus: boolean): void {
        this.play(`bonusSymbolCoin`, false, intoBonus ? this.settings.config.bonusSymbolCoinIntoBonusVolume : undefined);

        Tween.get(this)
        .wait(this.settings.config.bonusSymbolCoinWooshDelay)
        .call(() => {
            this.play(`bonusSymbolCoinWoosh`);
        });
    }

    bonusSymbolCoinLand(): void {
        this.play(`bonusSymbolCoinLand`);
    }

    winRollUp(stopDelay: number, freeSpins: boolean): void {
        const sound = this.play(freeSpins ? `freeSpinWinRollUp` : `baseWinRollUp`, true);

        Tween.get(this.winSoundObject)
        .wait(stopDelay)
        .call(() => {

            sound?.stop();
            this.play(freeSpins ? `freeSpinWinRollUpTerm` : `baseWinRollUpTerm`);
        });
    }

    stopWinRollUp(bigWin: boolean, freeSpins: boolean): void {

        Tween.removeTweens(this.winSoundObject);

        const {sound} = this.getSoundData(freeSpins ? `freeSpinWinRollUp` : `baseWinRollUp`);
        sound?.stop();

        if (!bigWin) {
            this.play(freeSpins ? `freeSpinWinRollUpTerm` : `baseWinRollUpTerm`);
        }
    }

    consolation(): void {
        this.play(`consolation`);
    }

    winningSymbolSound(symbolID?: SymbolID): void {

        if (symbolID) {

            this.winningSymbolSoundID = symbolID;
            this.play(`symbolWin` + symbolID);

            const {sound} = this.getSoundData(`bonusTheme`);

            if (sound && sound.playing()) {

                sound.volume(0);

                Tween.get(this.bonusFadeObject)
                .wait(this.settings.config.bonusThemeSymbolWinFadeInDelay)
                .call(() => {
                    sound?.fade(
                        0,
                        this.settings.config.bonusThemeSymbolWinFadeInVolume,
                        this.settings.config.bonusThemeFadeTime
                    );
                });
            }
        }
    }

    stopWinningSymbolSound(): void {

        if (this.winningSymbolSoundID) {

            let {sound, volume} = this.getSoundData(`symbolWin` + this.winningSymbolSoundID);
            sound?.stop();

            ({sound, volume} = this.getSoundData(`bonusTheme`));

            if (sound && sound.playing()) {

                Tween.removeTweens(this.bonusFadeObject);

                const currentVolume = sound.volume();
                sound.fade(currentVolume, volume, this.settings.config.bonusThemeFadeTime);
            }

            this.winningSymbolSoundID = undefined;
        }
    }

    revealMultiPanel(): void {
        this.play(`multipanel${++this.multiPanelIndex}`);
    }

    transitionMultiPanel(): void {
        this.play(`multipanelTransition${++this.multiPanelIndex}`);
    }

    resetMultiPanelIndex(): void {
        this.multiPanelIndex = -1;
    }

    bonusSymbolWin(): void {
        this.play(`bonusSymbolWin`);
    }

    bonusBell(): void {
        this.play(`bonusBell`);
    }

    bonusBellRetrigger(): void {
        this.play(`bonusBellRetrigger`);

        Tween.get(this)
        .wait(this.settings.config.bonusBellRetriggerLength)
        .call(() => this.signals.bonusBellRetriggerComplete.dispatch());
    }

    bonusTheme(): void {

        Tween.get(this)
        .wait(this.settings.config.bonusThemeStartDelay)
        .call(() => this.play(`bonusTheme`, true));
    }

    fadeOutBonusTheme(): void {

        const {sound, volume} = this.getSoundData(`bonusTheme`);
        sound?.fade(volume, 0, this.settings.config.bonusThemeFadeTime);
    }

    fadeInBonusTheme(): void {

        const {sound, volume} = this.getSoundData(`bonusTheme`);
        sound?.fade(0, volume, this.settings.config.bonusThemeFadeTime);
    }

    stopBonusTheme(): void {

        const {sound, volume} = this.getSoundData(`bonusTheme`);

        if (sound && sound.playing()) {

            const {bonusThemeFadeTime} = this.settings.config;

            sound?.fade(volume, 0, bonusThemeFadeTime);

            Tween.get(this)
            .wait(bonusThemeFadeTime)
            .call(() => sound?.stop());
        }
    }

    wheelIntro(): void {
        this.play(`wheelIntro`);
    }

    wheelOutro(): void {
        this.play(`wheelOutro`);
    }

    wheelClick(): void {
        const now = Date.now();
        if (now - this.lastWheelClick < (1000 / this.maxWheelClicksPerSecond)) {
            return;
        }
        this.lastWheelClick = now;
        const sound = this.play(`wheelClick${random(WHEEL_CLICK_SOUNDS-1)}`, false, undefined, true);
        if (sound) { // wheel clicks are new instances so clean up after complete
            sound.on(`end`, () => sound.unload());
        }
    }

    wheelPrize(wedgeID: WedgeID): void {

        if (wedgeID in CashWedgeID) {
            this.play(`wheelPrizeCash`);

        } else if (wedgeID in FreeSpinWedgeID) {
            this.play(`wheelPrizeFreespins`);

        } else if (wedgeID in JackpotWedgeID) {
            this.play(`wheelPrizeJackpot`);

        } else {
            this.play(`wheelPrizeMulti`);
        }
    }

    wheelPrizeJackpotCelebration(): void {
        this.play(`wheelPrizeJackpotCelebration`);
    }

    stopWheelPrizeJackpotCelebration(): void {

        const {sound} = this.getSoundData(`wheelPrizeJackpotCelebration`);
        sound?.stop();
    }

    startJackpotBowlCoinPour(): void {
        this.play(`jackpotBowlCoinPour`, true);
    }

    stopJackpotBowlCoinPour(): void {

        const {sound} = this.getSoundData(`jackpotBowlCoinPour`);
        sound?.stop();
    }

    freeSpinCounterIncrement(): void {

        const {sound} = this.getSoundData(`freeSpinCounterIncrement`);
        sound?.stop();

        this.play(`freeSpinCounterIncrement`);
    }

    scrollOpen(): void {

        Tween.get(this)
        .wait(this.settings.config.scrollOpenDelay)
        .call(() => this.play(`scrollOpen`));
    }

    exitFreeSpinsTransition(): void {
        this.play(`exitFreeSpinsTransition`);
    }

    bigWinCountUp(): void {
        this.play(`bigWinCountUp`);
    }

    bigWinSting(): void {
        this.play(`bigWinSting`);
    }

    stopBigWinCountUp(): void {

        const {sound} = this.getSoundData(`bigWinCountUp`);
        sound?.stop();
    }

    uiClick(): void {
        this.play(`uiClick`);
    }

    touchBuddhaLaugh(): void {
        this.play(`laugh`);
    }

    reset(): void {

        Tween.removeTweens(this);
        Tween.removeTweens(this.winSoundObject);
        Tween.removeTweens(this.bonusFadeObject);

        for (const sound of Object.values(this.sounds)) {
            sound.stop();
        }

        this.spinIndex = -1;
        this.multiPanelIndex = -1;
        this.winningSymbolSoundID = undefined;
    }

    private play(name: string, loop=false, vol?: number, newInstance=false): Howl | undefined {

        const {sound, volume} = this.getSoundData(name, newInstance);

        if (sound) {

            const v = (vol) ? vol/100 : volume;

            sound.stop();
            sound.volume(v);
            sound.loop(loop);
            sound.play();

            return sound;
        }
    }

    private getSoundData(key: string, newInstance=false): {sound?: Howl, volume: number} {

        const data = this.settings.data[key];

        const sound = newInstance ? this.getSoundInstance(data.name) : this.getSound(data.name);
        const volume = (data.volume > 0) ? data.volume/100 : data.volume;

        return {sound, volume};
    }

    private getSoundInstance(sound: string): Howl | undefined {
        if (this.services.audio.soundReady === true) {
            var howl = new Howl({
                src: this.services.resources.getAudio(sound + `.m4a`)
            });
            return howl;
        }
    }

    private getSound(sound: string): Howl | undefined {

        if (this.services.audio.soundReady === true) {

            const existingSound = this.sounds[sound];

            if (existingSound) {
                return existingSound;
            } else {

                var howl = new Howl({
                    src: this.services.resources.getAudio(sound + `.m4a`)
                });

                this.sounds[sound] = howl;

                return howl;
            }
        }
    }

    private fadeOutSpinSound(freeSpin: boolean): number {

        const newVolume = this.settings.config.precogFadeOutSpinVolume/100;
        const fadeTime = this.settings.config.precogFadeOutSpinTime;

        if (freeSpin) {
            const {sound, volume} = this.getSoundData(`bonusTheme`);
            sound?.fade(volume, newVolume, fadeTime);

        } else {

            if (this.spinIndex > 0) {

                const {sound, volume} = this.getSoundData(`spin${this.spinIndex}`);
                sound?.fade(volume, newVolume, fadeTime);
            }
        }

        return newVolume;
    }
}